import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiPageResponse } from '../types/api.types';
import { rsql } from '../helpers/rsql';
import { Chat, Message } from '../types/chat.types';

/**
 * Chat de location — rental-service via gateway (CHAT_FLOW.md). Le temps réel
 * (nouveau message) passe par une notif `NEW_CHAT` de notification-service
 * (WebSocket déjà câblé) — ce service ne couvre que le REST.
 */
@Injectable({ providedIn: 'root' })
export class ChatsService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/rental/api`;

  /** Mes conversations — `GET /chats` (enveloppe Nawam, 1-indexée). */
  list(page = 1, size = 20): Observable<ApiPageResponse<Chat>> {
    const params = new HttpParams()
      .set('pageable', 'true')
      .set('page', String(page))
      .set('size', String(size));
    return this.http.get<ApiPageResponse<Chat>>(`${this.base}/chats`, { params });
  }

  /** Ouvrir une conversation (2 participants) — `GET /chats/me/{id}`. 403 si non participant. */
  getMine(id: number): Observable<Chat> {
    return this.http.get<Chat>(`${this.base}/chats/me/${id}`);
  }

  /** Retrouve le chat d'une location — `GET /chats?search=rental.id=={rentalId}`.
   *  `null` si aucun (chat créé seulement à partir de CONFIRMED). */
  forRental(rentalId: number): Observable<Chat | null> {
    const params = new HttpParams()
      .set('pageable', 'true')
      .set('page', '1')
      .set('size', '1')
      .set('search', rsql.eq('rental.id', rentalId) ?? '');
    return this.http
      .get<ApiPageResponse<Chat>>(`${this.base}/chats`, { params })
      .pipe(map((res) => res.data?.[0] ?? null));
  }

  /** Fil d'un chat — `GET /messages?search=chat.id=={chatId}` (scopé serveur, tri desc). */
  messages(chatId: number, page = 1, size = 40): Observable<ApiPageResponse<Message>> {
    const params = new HttpParams()
      .set('pageable', 'true')
      .set('page', String(page))
      .set('size', String(size))
      .set('search', rsql.eq('chat.id', chatId) ?? '');
    return this.http.get<ApiPageResponse<Message>>(`${this.base}/messages`, { params });
  }

  /** Envoyer un message — `POST /messages` (body minimal). 400 si chat clôturé. */
  send(chatId: number, content: string): Observable<Message> {
    return this.http.post<Message>(`${this.base}/messages`, {
      content,
      chat: { id: chatId },
    });
  }
}
